import {
  Drawer,
  Form,
  Select,
  Button,
  Space,
  Spin,
  message,
  Row,
  Col,
  Typography,
  Divider,
  Popconfirm,
} from "antd"
import { useEffect, useState } from "react"
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  getDoc,
  query,
  where,
} from "firebase/firestore"
import { db } from "../../firebase/config"
import { SaveOutlined, CloseOutlined, DeleteOutlined } from "@ant-design/icons"

const { Text } = Typography

const ORDER_LOCATIONS = [
  { value: 0, label: "Manager" },
  { value: 1, label: "Designer" },
  { value: 2, label: "Paper Manager" },
  { value: 3, label: "Arsoma" },
  { value: 3.5, label: "Tape" },
  { value: 4, label: "Viva" },
  { value: 5, label: "Winder" },
  { value: 6, label: "Warehouse" },
  { value: 6.5, label: "Delivery & Payment" },
  { value: 7, label: "Delivered" },
  { value: 996, label: "Complaints" },
  { value: 997, label: "Deletion Process" },
  { value: 998, label: "Order On Hold" },
  { value: 999, label: "Stopped" },
]

export default function OrderEditDrawer({ order, isOpen, onClose }) {
  const [form] = Form.useForm()
  const [machines, setMachines] = useState([])
  const [manufacturers, setManufacturers] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [selectedMachine, setSelectedMachine] = useState(
    order?.selectedProduct?.machine || ""
  )
  const [selectedLocation, setSelectedLocation] = useState(order?.orderStatus || 0)
  const [selectedManufacturer, setSelectedManufacturer] = useState(
    order?.manufacturer?.id || ""
  )
  const [selectedClient, setSelectedClient] = useState(order?.clientId || "")

  // Fetch machines and manufacturers on drawer open
  useEffect(() => {
    if (!isOpen) return

    const fetchData = async () => {
      setLoading(true)
      try {
        // Fetch machines
        const machinesRef = collection(db, "machines")
        const machinesSnapshot = await getDocs(machinesRef)
        const machinesList = machinesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        setMachines(machinesList)

        // Fetch manufacturers
        const manufacturersRef = collection(db, "manufacturers")
        const manufacturersSnapshot = await getDocs(manufacturersRef)
        const manufacturersList = manufacturersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        setManufacturers(manufacturersList)

        // Fetch clients
        const clientsRef = collection(db, "clients")
        const clientsSnapshot = await getDocs(clientsRef)
        const clientsList = clientsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        setClients(clientsList)
      } catch (error) {
        message.error("Error loading data: " + error.message)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [isOpen])

  // Initialize form with current values
  useEffect(() => {
    if (order && isOpen) {
      setSelectedMachine(order?.selectedProduct?.machine || "")
      setSelectedLocation(order?.orderStatus || 0)
      setSelectedManufacturer(order?.manufacturer?.id || "")
      setSelectedClient(order?.clientId || "")
      form.setFieldsValue({
        machine: order?.selectedProduct?.machine || "",
        location: order?.orderStatus || 0,
        manufacturer: order?.manufacturer?.id || "",
        client: order?.clientId || "",
      })
    }
  }, [order, isOpen, form])

  // Get all locations with disabled state for forward movement and machine constraints
  const getLocationOptions = () => {
    const currentLocation = order?.orderStatus || 0
    const currentMachine = order?.selectedProduct?.machine

    return ORDER_LOCATIONS.map((loc) => {
      // Special statuses are always enabled
      if (loc.value >= 996) {
        return {
          label: loc.label,
          value: loc.value,
          disabled: false,
        }
      }

      let isDisabled = false

      // Rule 1: Disable forward movement (higher values)
      if (loc.value > currentLocation) {
        isDisabled = true
      }

      // Rule 2: Machine-specific constraints
      if (currentMachine === "Tape") {
        // Tape cannot move to Arsoma (3) or Viva (4)
        if (loc.value === 3 || loc.value === 4) {
          isDisabled = true
        }
      } else if (currentMachine === "Arsoma" || currentMachine === "Viva") {
        // Arsoma and Viva cannot move to Tape (3.5)
        if (loc.value === 3.5) {
          isDisabled = true
        }
      }

      return {
        label: loc.label,
        value: loc.value,
        disabled: isDisabled,
      }
    })
  }

  // Handle save
  const handleSave = async () => {
    try {
      setSaving(true)

      // Find the selected machine data
      const machineData = machines.find((m) => m.machine === selectedMachine)
      if (!machineData) {
        message.error("Selected machine not found")
        return
      }

      // Find the selected manufacturer data
      const manufacturerData = manufacturers.find((m) => m.id === selectedManufacturer)
      if (!manufacturerData) {
        message.error("Selected manufacturer not found")
        return
      }

      // Update the order document
      const orderRef = doc(db, "orders", order.id)
      const updateData = {
        "selectedProduct.machine": selectedMachine,
        "selectedProduct.machineCosts": machineData.costs || {
          amortization: 0,
          electricity: 0,
          worker: 0,
        },
        manufacturer: manufacturerData,
      }

      // Only update orderStatus if it changed
      if (selectedLocation !== order?.orderStatus) {
        updateData.orderStatus = selectedLocation
      }

      // Handle client change if it changed
      if (selectedClient !== order?.clientId) {
        const newClientData = clients.find((c) => c.id === selectedClient)
        if (!newClientData) {
          message.error("Selected client not found")
          return
        }

        updateData.client = newClientData
        updateData.clientId = selectedClient

        // If client changed and there's a price, adjust credits
        if (order.price) {
          const orderPrice = Number(order.price)

          // Refund to old client
          if (order.clientId) {
            const oldClientRef = doc(db, "clients", order.clientId)
            const oldClientSnap = await getDoc(oldClientRef)
            if (oldClientSnap.exists()) {
              const oldClientData = oldClientSnap.data()
              const oldCreditUsed = Number(oldClientData.creditUsed || 0)
              const oldCreditLeft = Number(oldClientData.creditLeft || 0)
              const newOldCreditUsed = Math.max(0, oldCreditUsed - orderPrice)
              const newOldCreditLeft = oldCreditLeft + orderPrice

              await updateDoc(oldClientRef, {
                creditUsed: newOldCreditUsed,
                creditLeft: newOldCreditLeft,
              })
            }
          }

          // Charge new client
          const newClientRef = doc(db, "clients", selectedClient)
          const newClientSnap = await getDoc(newClientRef)
          if (newClientSnap.exists()) {
            const newClientDataSnap = newClientSnap.data()
            const newCreditUsed = Number(newClientDataSnap.creditUsed || 0)
            const newCreditLeft = Number(newClientDataSnap.creditLeft || 0)
            const newNewCreditUsed = newCreditUsed + orderPrice
            const newNewCreditLeft = Math.max(0, newCreditLeft - orderPrice)

            await updateDoc(newClientRef, {
              creditUsed: newNewCreditUsed,
              creditLeft: newNewCreditLeft,
            })
          }
        }
      }

      await updateDoc(orderRef, updateData)

      message.success("Saved!")
      onClose()
    } catch (error) {
      message.error("Error: " + error.message)
    } finally {
      setSaving(false)
    }
  }

  // Handle delete
  const handleDelete = async () => {
    try {
      setDeleting(true)

      // Save to deletedOrders collection
      const deletedOrdersRef = collection(db, "deletedOrders")
      await addDoc(deletedOrdersRef, {
        ...order,
        deletedAt: new Date(),
        deletedFrom: "orders",
      })

      // Delete from orders collection
      const orderRef = doc(db, "orders", order.id)
      await deleteDoc(orderRef)

      // Update activeOrderCount in manufacturer
      if (order.manufacturer?.id) {
        const manufacturerRef = doc(db, "manufacturers", order.manufacturer.id)
        const manufacturerSnap = await getDoc(manufacturerRef)

        if (manufacturerSnap.exists()) {
          const manufacturerData = manufacturerSnap.data()
          let currentCount = manufacturerData.activeOrderCount || 0

          // Decrease count but ensure it doesn't go below 0
          const newCount = Math.max(0, currentCount - 1)

          await updateDoc(manufacturerRef, {
            activeOrderCount: newCount,
          })
        }
      }

      // Refund credit to client
      if (order.clientId && order.price) {
        const clientDocRef = doc(db, "clients", order.clientId)
        const clientSnap = await getDoc(clientDocRef)

        if (clientSnap.exists()) {
          const clientData = clientSnap.data()
          const currentCreditUsed = Number(clientData.creditUsed || 0)
          const currentCreditLeft = Number(clientData.creditLeft || 0)
          const orderCost = Number(order.price)

          // Refund credit: decrease creditUsed, increase creditLeft
          const newCreditUsed = Math.max(0, currentCreditUsed - orderCost)
          const newCreditLeft = currentCreditLeft + orderCost

          await updateDoc(clientDocRef, {
            creditUsed: newCreditUsed,
            creditLeft: newCreditLeft,
          })
        }
      }

      message.success("Order deleted and credit refunded!")
      onClose()
    } catch (error) {
      message.error("Error deleting: " + error.message)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Drawer
      title={`Order ${order?.orderNumber}`}
      placement="right"
      onClose={onClose}
      open={isOpen}
      width={320}
      styles={{ body: { padding: "12px" } }}>
      <Spin spinning={loading}>
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          {/* Order Info */}
          <div style={{ fontSize: "12px" }}>
            <Text type="secondary">
              {order?.client?.client}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: "11px" }}>
              {order?.selectedProduct?.product?.productName}
            </Text>
          </div>

          <Divider style={{ margin: "8px 0" }} />

          {/* Machine & Manufacturer & Location Selection */}
          <Form form={form} layout="vertical" size="small">
            <Form.Item
              label={<span style={{ fontSize: "11px" }}>Machine</span>}
              name="machine"
              style={{ marginBottom: "8px" }}>
              <Select
                size="small"
                placeholder="Select"
                value={selectedMachine}
                onChange={setSelectedMachine}
                options={machines.map((machine) => ({
                  label: machine.machine,
                  value: machine.machine,
                }))}
              />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontSize: "11px" }}>Manufacturer</span>}
              name="manufacturer"
              style={{ marginBottom: "8px" }}>
              <Select
                size="small"
                placeholder="Select"
                value={selectedManufacturer}
                onChange={setSelectedManufacturer}
                options={manufacturers.map((manufacturer) => ({
                  label: manufacturer.company,
                  value: manufacturer.id,
                }))}
              />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontSize: "11px" }}>Location</span>}
              name="location"
              style={{ marginBottom: "8px" }}>
              <Select
                size="small"
                placeholder="Select"
                value={selectedLocation}
                onChange={setSelectedLocation}
                options={getLocationOptions()}
              />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontSize: "11px" }}>Change Client</span>}
              name="client"
              style={{ marginBottom: "8px" }}>
              <Select
                size="small"
                placeholder="Search clients..."
                value={selectedClient}
                onChange={setSelectedClient}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={clients.map((client) => ({
                  label: client.client,
                  value: client.id,
                }))}
                showSearch
              />
            </Form.Item>
          </Form>

          <Divider style={{ margin: "8px 0" }} />

          {/* Action Buttons */}
          <Row gutter={[6, 6]}>
            <Col span={12}>
              <Popconfirm
                title="Delete Order?"
                description="This will move the order to deletedOrders. Continue?"
                onConfirm={handleDelete}
                okText="Yes"
                cancelText="No">
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  loading={deleting}
                  style={{ width: "100%", fontSize: "11px" }}
                  block>
                  Delete
                </Button>
              </Popconfirm>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
                style={{ width: "100%", fontSize: "11px" }}
                block>
                Save
              </Button>
            </Col>
          </Row>

          <Button
            type="default"
            size="small"
            icon={<CloseOutlined />}
            onClick={onClose}
            style={{ width: "100%", fontSize: "11px" }}
            block>
            Close
          </Button>
        </Space>
      </Spin>
    </Drawer>
  )
}
